import React, { useEffect, useMemo, useRef, useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";
import { QUICK_RANGE_OPTIONS } from "./constants";

dayjs.extend(isBetween);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const formatRangeLabel = (start, end) => {
  if (!start || !end) return "Select date or range";
  if (start.year() === end.year()) {
    if (start.month() === end.month()) {
      return `${start.format("MMM D")} - ${end.format("D, YYYY")}`;
    }
    return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
  }
  return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
};

const getQuickRange = (label) => {
  const today = dayjs().startOf("day");
  switch (label) {
    case "Today":
      return [today, today];
    case "Yesterday": {
      const yesterday = today.subtract(1, "day");
      return [yesterday, yesterday];
    }
    case "This week":
      return [today.startOf("week"), today.endOf("week")];
    case "Last week": {
      const lastWeek = today.subtract(1, "week");
      return [lastWeek.startOf("week"), lastWeek.endOf("week")];
    }
    case "This month":
      return [today.startOf("month"), today.endOf("month")];
    case "Last month": {
      const lastMonth = today.subtract(1, "month");
      return [lastMonth.startOf("month"), lastMonth.endOf("month")];
    }
    case "This year":
      return [today.startOf("year"), today.endOf("year")];
    case "Last year": {
      const lastYear = today.subtract(1, "year");
      return [lastYear.startOf("year"), lastYear.endOf("year")];
    }
    case "All time":
      return [dayjs("2020-01-01"), today];
    default:
      return [today.subtract(29, "day"), today];
  }
};

const RangeDay = ({
  start,
  end,
  day,
  outsideCurrentMonth,
  isFirstVisibleCell,
  isLastVisibleCell,
  ...other
}) => {
  if (outsideCurrentMonth) {
    return (
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        selected={false}
        disableMargin
      />
    );
  }

  const isStart = start && day.isSame(start, "day");
  const isEnd = end && day.isSame(end, "day");
  const isSameDay = isStart && isEnd;
  const inRange =
    start &&
    end &&
    !isSameDay &&
    day.isAfter(start, "day") &&
    day.isBefore(end, "day");

  const hasRange = Boolean(start && end && !isSameDay);
  const isRangeDay = isStart || isEnd || inRange;

  const nextDay = day.add(1, "day");

  const isSegmentRowStart =
    hasRange &&
    (inRange || (isEnd && !isStart)) &&
    (isFirstVisibleCell || day.day() === 0);

  const isSegmentRowEnd =
    hasRange &&
    (inRange || (isStart && !isEnd)) &&
    (isLastVisibleCell ||
      day.day() === 6 ||
      (inRange &&
        (nextDay.isBefore(end, "day") || nextDay.isSame(end, "day")) &&
        nextDay.day() === 0));

  if (!isRangeDay) {
    return (
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        selected={false}
        disableMargin
      />
    );
  }

  return (
    <div
      className={[
        "SmsAnalyticsRangeDayWrapper",
        isStart && "SmsAnalyticsRangeDayWrapperStart",
        isEnd && "SmsAnalyticsRangeDayWrapperEnd",
        inRange && "SmsAnalyticsRangeDayWrapperInRange",
        isSegmentRowStart && "SmsAnalyticsRangeDayWrapperRowStart",
        isSegmentRowEnd && "SmsAnalyticsRangeDayWrapperRowEnd",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <PickersDay
        {...other}
        day={day}
        outsideCurrentMonth={outsideCurrentMonth}
        disableMargin
        selected={false}
        className={other.className}
      />
    </div>
  );
};

const calendarSx = {
  width: 280,
  "& .MuiPickersCalendarHeader-label": {
    fontSize: "13px",
    fontWeight: 600,
  },
  "& .MuiPickersDay-root": {
    fontSize: "13px",
    margin: "0 !important",
  },
  "& .MuiPickersDay-dayWithMargin": {
    margin: "0 !important",
  },
  "& .MuiDayCalendar-weekContainer": {
    margin: 0,
    display: "grid",
    gridTemplateColumns: "repeat(7, 36px)",
    justifyContent: "center",
  },
  "& .MuiDayCalendar-header": {
    display: "grid",
    gridTemplateColumns: "repeat(7, 36px)",
    justifyContent: "center",
    margin: "0 0 4px",
  },
  "& .MuiDayCalendar-weekDayLabel": {
    width: 36,
    margin: 0,
  },
};

const AnalyticsDateRangePicker = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(value?.[0] ?? null);
  const [draftEnd, setDraftEnd] = useState(value?.[1] ?? null);
  const [activeQuickRange, setActiveQuickRange] = useState(null);
  const [leftMonth, setLeftMonth] = useState(() =>
    (value?.[0] ?? dayjs()).startOf("month")
  );
  const containerRef = useRef(null);

  const rightMonth = useMemo(
    () => leftMonth.startOf("month").add(1, "month"),
    [leftMonth]
  );

  useEffect(() => {
    if (value?.[0] && value?.[1]) {
      setDraftStart(value[0]);
      setDraftEnd(value[1]);
      setLeftMonth(value[0].startOf("month"));
      return;
    }

    setDraftStart(null);
    setDraftEnd(null);
    setActiveQuickRange(null);
    setLeftMonth(dayjs().startOf("month"));
  }, [value]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleDaySelect = (day) => {
    if (!draftStart || (draftStart && draftEnd)) {
      setDraftStart(day);
      setDraftEnd(null);
      return;
    }
    if (day.isBefore(draftStart, "day")) {
      setDraftEnd(draftStart);
      setDraftStart(day);
    } else {
      setDraftEnd(day);
    }
    setActiveQuickRange(null);
  };

  const handleQuickRange = (label) => {
    const [start, end] = getQuickRange(label);
    setDraftStart(start);
    setDraftEnd(end);
    setLeftMonth(start.startOf("month"));
    setActiveQuickRange(label);
  };

  const handleApply = () => {
    if (draftStart && draftEnd) {
      onChange?.([draftStart, draftEnd]);
      setOpen(false);
    }
  };

  const handleCancel = () => {
    if (value?.[0] && value?.[1]) {
      setDraftStart(value[0]);
      setDraftEnd(value[1]);
    } else {
      setDraftStart(null);
      setDraftEnd(null);
      setActiveQuickRange(null);
    }
    setOpen(false);
  };

  const displayStart = value?.[0];
  const displayEnd = value?.[1];

  return (
    <div className="SmsAnalyticsDateRangePicker" ref={containerRef}>
      <button
        type="button"
        className="SmsAnalyticsDateRangeButton"
        onClick={() => setOpen((prev) => !prev)}
      >
        <img alt="" src="/media/icons/calendar.svg" className="w-4 h-4" />
        {formatRangeLabel(displayStart, displayEnd)}
        <img
          alt=""
          src={
            open ? "/media/icons/chevronUp.svg" : "/media/icons/chevronDown.svg"
          }
          className="w-[14px] h-[14px]"
        />
      </button>

      {open && (
        <div className="SmsAnalyticsDateRangePopover">
          <div className="SmsAnalyticsDateRangeBody">
            <div className="SmsAnalyticsDateRangeQuickRanges">
              <div className="SmsAnalyticsDateRangeQuickRangesTitle">
                QUICK RANGES
              </div>
              {QUICK_RANGE_OPTIONS.map((label) => (
                <button
                  key={label}
                  type="button"
                  className={"SmsAnalyticsDateRangeQuickItem"}
                  onClick={() => handleQuickRange(label)}
                >
                  <span>{label}</span>
                  {activeQuickRange === label && (
                    <img
                      alt=""
                      src="/media/icons/check.svg"
                      className="w-4 h-4"
                    />
                  )}
                </button>
              ))}
            </div>

            <div className="SmsAnalyticsDateRangeCalendars">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateCalendar
                  referenceDate={leftMonth}
                  onMonthChange={(month) =>
                    setLeftMonth(month.startOf("month"))
                  }
                  onChange={handleDaySelect}
                  slots={{
                    day: (props) => (
                      <RangeDay {...props} start={draftStart} end={draftEnd} />
                    ),
                  }}
                  sx={calendarSx}
                />
                <DateCalendar
                  key={rightMonth.format("YYYY-MM")}
                  referenceDate={rightMonth}
                  onMonthChange={(month) =>
                    setLeftMonth(month.startOf("month").subtract(1, "month"))
                  }
                  onChange={handleDaySelect}
                  slots={{
                    day: (props) => (
                      <RangeDay {...props} start={draftStart} end={draftEnd} />
                    ),
                  }}
                  sx={calendarSx}
                />
              </LocalizationProvider>
            </div>
          </div>

          <div className="SmsAnalyticsDateRangeFooter">
            <div className="SmsAnalyticsDateRangeInputs">
              <input
                readOnly
                className="SmsAnalyticsDateRangeInput"
                value={draftStart ? draftStart.format("MMM D, YYYY") : ""}
              />
              <span className="SmsAnalyticsDateRangeDash">–</span>
              <input
                readOnly
                className="SmsAnalyticsDateRangeInput"
                value={draftEnd ? draftEnd.format("MMM D, YYYY") : ""}
              />
            </div>
            <div className="SmsAnalyticsDateRangeActions">
              <button
                type="button"
                className="SmsAnalyticsDateRangeCancel"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="SmsAnalyticsDateRangeApply"
                onClick={handleApply}
                disabled={!draftStart || !draftEnd}
              >
                Apply
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AnalyticsDateRangePicker;
